import React from 'react';
import { Box, Heading, Text, Button, VStack, Alert, AlertIcon, AlertTitle, AlertDescription } from '@chakra-ui/react';

// Full-page error boundary for critical app-level errors
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error boundary caught:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          minH="100vh"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bg="gray.50"
          p={4}
        >
          <VStack spacing={6} textAlign="center">
            <Heading size="xl" color="red.500">Something went wrong</Heading>
            <Text color="gray.600" maxW="400px">
              An unexpected error occurred. Please try refreshing the page or contact support if the problem persists.
            </Text>
            <Button colorScheme="red" onClick={this.handleReset}>
              Return to Home
            </Button>
          </VStack>
        </Box>
      );
    }

    return this.props.children;
  }
}

// Route-level error boundary for individual page errors
// Allows recovery without losing entire app state
export class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Route error boundary caught:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box p={6}>
          <Alert
            status="error"
            variant="subtle"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            borderRadius="lg"
            py={10}
          >
            <AlertIcon boxSize="40px" mr={0} />
            <AlertTitle mt={4} mb={1} fontSize="lg">
              This page encountered an error
            </AlertTitle>
            <AlertDescription maxWidth="sm" mb={4}>
              Something went wrong loading this page. You can try again or navigate to another section.
            </AlertDescription>
            <Button colorScheme="red" size="sm" onClick={this.handleRetry}>
              Try Again
            </Button>
          </Alert>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
